import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { API, COLORS } from '../utils/constants';
import { compressImage } from '../utils/helpers';

export default function ImagePicker({ endpoint, title, description, stripDataUrlPrefix = true, skipSuccessCheck = false, onResult, onError, onLoading, children }) {
  const [photos, setPhotos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instrucciones, setInstrucciones] = useState("");
  const [retryMode, setRetryMode] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null); // dataUrl tras captura
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null); // solo para añadir desde fototeca en el modal

  // Arrancar cámara
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch {
      // Si falla getUserMedia, caemos al input file clásico
      stopCamera();
      setCameraOpen(false);
      if (fileInputRef.current) fileInputRef.current.click();
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (cameraOpen && !pendingPhoto) startCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOpen, pendingPhoto]);

  // Bloquear scroll del body mientras la cámara está abierta (iOS Safari)
  useEffect(() => {
    if (cameraOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [cameraOpen]);

  useEffect(() => () => stopCamera(), []);

  // Capturar frame del video
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    stopCamera();
    setPendingPhoto(dataUrl);
  };

  // Repetir: volver a la cámara
  const handleRepeat = () => {
    setPendingPhoto(null);
    // useEffect re-arranca la cámara al quedar cameraOpen=true y pendingPhoto=null
  };

  // Adjuntar y seguir tomando
  const handleAttach = () => {
    setPhotos(p => [...p, { dataUrl: pendingPhoto }]);
    setPendingPhoto(null);
    // useEffect re-arranca la cámara
  };

  // Adjuntar y revisar
  const handleAttachAndReview = () => {
    setPhotos(p => [...p, { dataUrl: pendingPhoto }]);
    setPendingPhoto(null);
    setCameraOpen(false);
    setShowModal(true);
  };

  // Cerrar cámara sin adjuntar
  const closeCamera = () => {
    stopCamera();
    setPendingPhoto(null);
    setCameraOpen(false);
  };

  const triggerOpen = () => {
    if (!loading) { setRetryMode(false); setCameraOpen(true); }
  };

  const analyzePhotos = async (currentPhotos, currentInstrucciones) => {
    if (currentPhotos.length === 0) return;
    setLoading(true);
    if (onLoading) onLoading(true);
    try {
      const images = stripDataUrlPrefix
        ? currentPhotos.map(p => p.dataUrl.split(",")[1])
        : currentPhotos.map(p => p.dataUrl);
      const body = { images };
      if (currentInstrucciones && currentInstrucciones.trim()) body.instrucciones = currentInstrucciones.trim();
      const res = await fetch(API + endpoint, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) {
        setRetryMode(true); setShowModal(true);
        if (onError) onError(data.error || "No se pudo analizar");
      } else {
        setShowModal(false); setPhotos([]); setInstrucciones("");
        if (onResult) onResult(data);
      }
    } catch {
      setRetryMode(true); setShowModal(true);
      if (onError) onError("Error de red");
    }
    setLoading(false);
    if (onLoading) onLoading(false);
  };

  const analyze = () => analyzePhotos(photos, instrucciones);

  const closeModal = () => {
    setShowModal(false); setPhotos([]); setInstrucciones(""); setRetryMode(false);
  };

  const handleFilesFromLibrary = async (files) => {
    const newPhotos = await Promise.all(
      Array.from(files).map(async (file) => {
        const dataUrl = await compressImage(file);
        return { dataUrl };
      })
    );
    setPhotos(p => [...p, ...newPhotos]);
  };

  const C = COLORS;

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
        onChange={e => { if (e.target.files.length > 0) handleFilesFromLibrary(e.target.files); e.target.value = ""; }} />

      {/* ── Vista cámara — portal directo al body para escapar de cualquier stacking context ── */}
      {cameraOpen && ReactDOM.createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100dvh", background: "#000", zIndex: 9999, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 20, left: 0, right: 0, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 20px", zIndex: 10 }}>
            <button onClick={closeCamera} style={{ background: "rgba(0,0,0,0.5)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, padding: "8px 16px", cursor: "pointer" }}>✕ Cancelar</button>
            {photos.length > 0 && (
              <span style={{ background: C.accent, borderRadius: 20, color: "#fff", fontSize: 13, fontWeight: 800, padding: "6px 14px" }}>
                {photos.length} foto{photos.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {!pendingPhoto && (
            <>
              <video ref={videoRef} autoPlay playsInline muted style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", bottom: 60, left: 0, right: 0, display: "flex", justifyContent: "center", zIndex: 10 }}>
                <button onClick={capturePhoto} style={{ width: 72, height: 72, borderRadius: "50%", background: "#fff", border: "4px solid rgba(255,255,255,0.4)", cursor: "pointer", boxShadow: "0 0 0 3px rgba(255,255,255,0.2)" }} />
              </div>
            </>
          )}

          {pendingPhoto && (
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <img src={pendingPhoto} alt="" style={{ flex: 1, objectFit: "cover", width: "100%", minHeight: 0 }} />
              <div style={{ background: "#1a1a26", padding: "20px 20px 44px", display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
                <button onClick={handleAttach} style={{ padding: "15px 0", borderRadius: 14, background: "linear-gradient(135deg, " + C.accent + ", #e55a2b)", border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
                  Adjuntar y seguir tomando
                </button>
                <button onClick={handleAttachAndReview} style={{ padding: "15px 0", borderRadius: 14, background: C.surface, border: "1px solid " + C.border, color: C.text, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                  Adjuntar y revisar
                </button>
                <button onClick={handleRepeat} style={{ padding: "13px 0", borderRadius: 14, background: "transparent", border: "1px solid " + C.border, color: C.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Repetir foto
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* ── Modal revisión del lote ── */}
      {showModal && (
        <div onClick={closeModal} style={{ position: "fixed", inset: 0, background: "#000b", zIndex: 700, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#1a1a26", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "24px 20px 44px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ color: C.text, fontSize: 17, fontWeight: 800 }}>
                {retryMode ? "No se pudo analizar" : title}
              </span>
              <button onClick={closeModal} style={{ background: "none", border: "none", color: C.textMuted, fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            {retryMode && (
              <div style={{ background: "#ff2d5520", border: "1px solid #ff2d5550", borderRadius: 10, padding: "10px 14px", marginBottom: 14, color: "#ff2d55", fontSize: 13 }}>
                La IA no pudo leer la etiqueta. Añade más fotos o escribe indicaciones adicionales.
              </div>
            )}
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, marginBottom: 16 }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position: "relative", flexShrink: 0 }}>
                  <img src={p.dataUrl} alt="" style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 10, border: "1px solid " + C.border }} />
                  <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))} style={{ position: "absolute", top: -6, right: -6, background: "#ff2d55", border: "none", borderRadius: "50%", width: 20, height: 20, color: "#fff", fontSize: 12, cursor: "pointer" }}>✕</button>
                </div>
              ))}
              <div onClick={() => setCameraOpen(true)} style={{ width: 90, height: 90, borderRadius: 10, border: "2px dashed " + C.border, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, fontSize: 28, color: C.textMuted }}>+</div>
            </div>
            {description && !retryMode && <p style={{ color: C.textMuted, fontSize: 12, margin: "0 0 12px" }}>{description}</p>}
            <textarea
              value={instrucciones}
              onChange={e => setInstrucciones(e.target.value)}
              placeholder={retryMode ? "Describe el producto o añade detalles de la etiqueta..." : "Instrucciones adicionales (opcional)..."}
              rows={2}
              style={{ width: "100%", background: C.bg, border: "1px solid " + (retryMode ? "#ff2d5560" : C.border), borderRadius: 10, padding: "10px 12px", color: C.text, fontSize: 13, outline: "none", resize: "none", boxSizing: "border-box", marginBottom: 14, fontFamily: "inherit" }}
            />
            <button onClick={analyze} disabled={photos.length === 0} style={{ width: "100%", padding: 16, borderRadius: 14, background: photos.length === 0 ? C.bg : "linear-gradient(135deg, " + C.accent + ", #e55a2b)", border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
              {retryMode ? "REINTENTAR ANÁLISIS" : "ANALIZAR " + photos.length + " FOTO" + (photos.length > 1 ? "S" : "")}
            </button>
          </div>
        </div>
      )}

      {children(triggerOpen, loading)}
    </>
  );
}
